import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/components/chat/VideoNoteRecorder.jsx
// Grabador de notas de video corto desde el chat (cámara + micrófono).
// Flujo: abrir → vista previa en vivo → grabar (contador, límite) → detener →
//        previsualizar (reproducir/regrabar) → enviar como attachment_kind='video_note'.
// Maneja permisos/errores y hace fallback si el navegador no soporta MediaRecorder.
import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import {
  Videocam as CamIcon, Stop as StopIcon, Send as SendIcon, Replay as ReplayIcon,
  Close as CloseIcon, FiberManualRecord as RecDotIcon, PlayArrow as PlayIcon,
} from '@mui/icons-material';
import {
  VIDEO_NOTE_MAX_SECONDS, VIDEO_NOTE_MAX_BYTES, VIDEO_MIME_CANDIDATES, pickSupportedMime,
} from './chatMediaConstants';

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

export const videoRecordingSupported = () =>
  typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof window.MediaRecorder !== 'undefined';

const VideoNoteRecorder = ({ open, onClose, onSend }) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState('idle'); // idle | live | recording | preview
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [sending, setSending] = useState(false);

  const liveVideoRef = useRef(null);
  const previewVideoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const blobRef = useRef(null);
  const timerRef = useRef(null);
  const dimsRef = useRef({ width: null, height: null });

  useEffect(() => {
    if (open) initCamera();
    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const cleanup = () => {
    clearInterval(timerRef.current);
    try { if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop(); } catch { /* noop */ }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
  };

  const fullReset = () => {
    cleanup();
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    blobRef.current = null;
    setSeconds(0);
    setError(null);
    setSending(false);
    setPhase('idle');
  };

  const handleClose = () => { fullReset(); onClose?.(); };

  const initCamera = async () => {
    setError(null);
    if (!videoRecordingSupported()) {
      setError(t('hardcoded.components_chat_videonoterecorder.tu_navegador_no_soporta_grabacion'));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 640 }, facingMode: 'user' },
        audio: true,
      });
      streamRef.current = stream;
      const track = stream.getVideoTracks()[0];
      const settings = track?.getSettings?.() || {};
      dimsRef.current = { width: settings.width || null, height: settings.height || null };
      setPhase('live');
      setTimeout(() => { if (liveVideoRef.current) liveVideoRef.current.srcObject = stream; }, 0);
    } catch (e) {
      const denied = e?.name === 'NotAllowedError' || e?.name === 'SecurityError';
      setError(denied
        ? t('hardcoded.components_chat_videonoterecorder.permiso_de_camaramicrofono_denegado_activalo')
        : t('hardcoded.components_chat_videonoterecorder.no_se_pudo_acceder_a'));
    }
  };

  const startRec = () => {
    const stream = streamRef.current;
    if (!stream) return;
    const mime = pickSupportedMime(VIDEO_MIME_CANDIDATES);
    let rec;
    try {
      rec = new window.MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    } catch {
      setError(t('hardcoded.components_chat_videonoterecorder.no_se_pudo_iniciar_la'));
      return;
    }
    chunksRef.current = [];
    blobRef.current = null;
    rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const type = rec.mimeType || mime || 'video/webm';
      const blob = new Blob(chunksRef.current, { type });
      blobRef.current = blob;
      setPreviewUrl(URL.createObjectURL(blob));
    };
    rec.start(250);
    recorderRef.current = rec;
    setSeconds(0);
    setPhase('recording');
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        const n = s + 1;
        if (n >= VIDEO_NOTE_MAX_SECONDS) stopRec();
        return n;
      });
    }, 1000);
  };

  const stopRec = () => {
    clearInterval(timerRef.current);
    try { recorderRef.current?.stop(); } catch { /* noop */ }
    // Detener la cámara en vivo; el preview usará el blob grabado.
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setPhase('preview');
  };

  const reRecord = () => {
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    blobRef.current = null;
    setSeconds(0);
    initCamera();
  };

  const send = async () => {
    const blob = blobRef.current;
    if (!blob || !blob.size) return;
    if (blob.size > VIDEO_NOTE_MAX_BYTES) {
      setError(t('hardcoded.components_chat_videonoterecorder.el_video_supera_el_limite', { MathroundVIDEO_: Math.round(VIDEO_NOTE_MAX_BYTES / (1024 * 1024)) }));
      return;
    }
    setSending(true);
    const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
    const file = new File([blob], `nota-de-video-${Date.now()}.${ext}`, { type: blob.type });
    await onSend?.(file, {
      durationSeconds: seconds,
      width: dimsRef.current.width,
      height: dimsRef.current.height,
    });
    setSending(false);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, bgcolor: '#16181d', color: '#fff', overflow: 'hidden' } }}>
      <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <CamIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 800, flex: 1 }}>{t('hardcoded.components_chat_videonoterecorder.nota_de_video')}</Typography>
        <IconButton size="small" onClick={handleClose} sx={{ color: 'grey.400' }}><CloseIcon /></IconButton>
      </Stack>

      <Box sx={{ position: 'relative', bgcolor: '#000', aspectRatio: '1/1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {error ? (
          <Stack alignItems="center" spacing={1.5} sx={{ p: 3, textAlign: 'center' }}>
            <CamIcon sx={{ fontSize: 48, color: '#f87171' }} />
            <Typography variant="body2" sx={{ color: 'grey.300' }}>{error}</Typography>
            <Button variant="contained" onClick={initCamera}>{i18n.t('hardcoded.components_ai_airecommendationspanel.reintentar')}</Button>
          </Stack>
        ) : phase === 'preview' ? (
          <video ref={previewVideoRef} src={previewUrl || undefined} controls playsInline
            style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }} />
        ) : (
          <video ref={liveVideoRef} autoPlay playsInline muted
            style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#000' }} />
        )}

        {phase === 'recording' && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{
            position: 'absolute', top: 10, left: 10, px: 1, py: 0.3, borderRadius: 2,
            bgcolor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
          }}>
            <RecDotIcon sx={{ color: 'error.main', fontSize: 14, animation: 'pulse 1.2s infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.25 } } }} />
            <Typography variant="caption" sx={{ fontWeight: 700 }}>{fmt(seconds)} / {fmt(VIDEO_NOTE_MAX_SECONDS)}</Typography>
          </Stack>
        )}
      </Box>

      {!error && (
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ py: 1.75 }}>
          {phase === 'live' && (
            <Tooltip title={t('hardcoded.components_chat_videonoterecorder.grabar')}>
              <IconButton onClick={startRec} sx={{ width: 60, height: 60, bgcolor: '#dc2626', color: '#fff', '&:hover': { bgcolor: '#b91c1c' } }}>
                <RecDotIcon sx={{ fontSize: 30 }} />
              </IconButton>
            </Tooltip>
          )}
          {phase === 'recording' && (
            <Tooltip title={t('hardcoded.components_chat_videonoterecorder.detener')}>
              <IconButton onClick={stopRec} sx={{ width: 60, height: 60, bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}>
                <StopIcon sx={{ fontSize: 28 }} />
              </IconButton>
            </Tooltip>
          )}
          {phase === 'preview' && (
            <>
              <Tooltip title={t('hardcoded.components_chat_videonoterecorder.regrabar')}>
                <IconButton onClick={reRecord} sx={{ color: 'grey.300', bgcolor: 'rgba(255,255,255,0.08)' }}><ReplayIcon /></IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                onClick={send} disabled={sending}
                sx={{ borderRadius: 6, px: 3, bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}>
                {t('hardcoded.components_chat_videonoterecorder.enviar')}</Button>
            </>
          )}
          {phase === 'idle' && <CircularProgress size={22} sx={{ color: '#fff' }} />}
        </Stack>
      )}
    </Dialog>
  );
};

export default VideoNoteRecorder;
